import { Link } from "react-router-dom";
import Logo from "../components/Logo";

export default function LegalPage({ type = "privacy" }) {
  const isPrivacy = type === "privacy";
  const updated = "August 2026";

  return (
    <div className="min-h-screen relative noise-bg" data-testid={`${type}-page`}>
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-sm text-neutral-400 hover:text-white transition-colors">Home</Link>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <div className="overline mb-3">LEGAL</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
          {isPrivacy ? "Privacy Policy" : "Terms of Service"}
        </h1>
        <p className="text-neutral-500 mt-3 text-sm">Last updated: {updated}</p>

        <div className="mt-10 space-y-8 text-neutral-300 text-sm sm:text-base leading-relaxed">
          {isPrivacy ? <PrivacyContent /> : <TermsContent />}
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-sm text-neutral-500">
          Questions? Email{" "}
          <a href="mailto:contact@freightpilotai.ai" className="text-[#FF5A1F] hover:text-[#FF7038]">contact@freightpilotai.ai</a>{" "}
          or call <a href="tel:+16056797303" className="text-[#FF5A1F] hover:text-[#FF7038]">(605) 679-7303</a>.
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} Freight Pilot AI LLC · Sioux Falls, South Dakota · FreightPilot AI™ ·{" "}
        <Link to="/privacy" className="hover:text-white">Privacy</Link> ·{" "}
        <Link to="/terms" className="hover:text-white">Terms</Link> ·{" "}
        <Link to="/status" className="hover:text-white">Status</Link>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl sm:text-2xl font-semibold text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function PrivacyContent() {
  return (
    <>
      <p>
        FreightPilot AI™ (operated by Freight Pilot AI LLC — "we," "our," or "us") respects your privacy. This policy explains what data we collect,
        how we use it, and your rights.
      </p>

      <Section title="1. Information we collect">
        <p><span className="text-white">Account data:</span> your name, email, company name, and password (hashed).</p>
        <p><span className="text-white">Operational data:</span> loads, drivers, invoices, maintenance records, applicants, and leads that you enter into the platform.</p>
        <p><span className="text-white">Communication data:</span> messages sent to our AI assistant.</p>
        <p><span className="text-white">Automatic data:</span> IP address, browser type, pages visited, timestamps, and cookies.</p>
      </Section>

      <Section title="2. How we use your information">
        <p>To operate the platform, generate AI-drafted content on your behalf, provide customer support, improve the product,
          and communicate with you about your account or new features.</p>
        <p>We do <span className="text-white">not</span> sell your data. We do not share your fleet's data with other fleets.</p>
      </Section>

      <Section title="3. AI and third-party processors">
        <p>AI-drafted messages are generated using OpenAI GPT models via a secure API. Your prompts are sent to OpenAI for processing
          but are not used by OpenAI to train their models per their business API terms.</p>
        <p><span className="text-white">Payments:</span> subscription payments are processed by Stripe. Your card details are entered on
          and stored by Stripe — we never see or store your full card number. See Stripe's privacy policy at stripe.com/privacy.</p>
        <p>We also use industry-standard providers for hosting, analytics (Meta Pixel), and email delivery. Each processor is bound
          by data processing agreements.</p>
      </Section>

      <Section title="4. Data security">
        <p>All connections use 256-bit SSL encryption. Passwords are hashed with bcrypt. Data is stored in private cloud infrastructure
          with restricted access. We take reasonable measures to prevent unauthorized access.</p>
      </Section>

      <Section title="5. Your rights">
        <p>You can access, export, or delete your data at any time by contacting us at contact@freightpilotai.ai.
          Deleting your account removes all associated fleet data permanently.</p>
      </Section>

      <Section title="6. Cookies and tracking">
        <p>We use minimal cookies for authentication and Meta Pixel for advertising analytics. You can disable cookies in your browser,
          though some features may not function properly.</p>
      </Section>

      <Section title="7. SMS / text messaging">
        <p>If you opt in to SMS notifications (through your account's notification settings), we collect your mobile phone
          number and your opt-in consent record so we can send you account notifications — for example load alerts, weekly
          performance recaps, and dispatch messages.</p>
        <p><span className="text-white">No mobile information will be shared with third parties or affiliates for marketing or
          promotional purposes.</span> Text messaging originator opt-in data and consent are never shared with any third parties.</p>
        <p>Message frequency varies. Message and data rates may apply. Reply <span className="text-white">STOP</span> to any
          message to opt out at any time, or reply <span className="text-white">HELP</span> for help.</p>
      </Section>

      <Section title="8. Children">
        <p>FreightPilot AI is not directed at anyone under 18 and does not knowingly collect data from minors.</p>
      </Section>

      <Section title="9. Changes">
        <p>We may update this policy. Material changes will be posted here with a new "Last updated" date and, when appropriate,
          announced via email.</p>
      </Section>

      <Section title="10. Contact">
        <p>Questions or requests: contact@freightpilotai.ai · (605) 679-7303 · Freight Pilot AI LLC, Sioux Falls, South Dakota.</p>
      </Section>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <p>
        These Terms of Service ("Terms") govern your use of FreightPilot AI™, operated by Freight Pilot AI LLC ("we," "our," "us").
        By creating an account or using the platform, you agree to these Terms.
      </p>

      <Section title="1. The service">
        <p>FreightPilot AI is a software-as-a-service platform for trucking operations, including dispatch, driver management, invoicing,
          maintenance tracking, recruiting, and AI-drafted communications.</p>
      </Section>

      <Section title="2. Your account">
        <p>You are responsible for maintaining the security of your account credentials and for all activity under your account.
          You agree to provide accurate information and to notify us of any unauthorized access.</p>
      </Section>

      <Section title="3. Acceptable use">
        <p>You agree not to use FreightPilot AI to: (a) violate any law, (b) infringe intellectual property, (c) transmit malicious code,
          (d) attempt to reverse-engineer the service, or (e) resell the platform without a written agreement.</p>
      </Section>

      <Section title="4. AI-generated content">
        <p>FreightPilot AI uses generative AI (OpenAI GPT models) to draft messages, invoices, and other content on your behalf. You are responsible
          for reviewing and approving all AI-drafted content before sending. We do not guarantee the accuracy of AI output and disclaim
          liability for content sent by you.</p>
      </Section>

      <Section title="5. Fees, billing and cancellation">
        <p><span className="text-white">Plans and trial:</span> every plan starts with a 7-day free trial. A payment card is required to start the trial; nothing is charged during the trial, and the plan you selected is billed automatically when the trial ends unless you cancel first.
          Current pricing is listed at freightpilotai.ai/pricing. Paid subscriptions are billed monthly in advance through our payment
          processor, Stripe.</p>
        <p><span className="text-white">Auto-renewal:</span> subscriptions renew automatically each month until canceled. You can cancel
          anytime from Billing → Manage subscription; cancellation takes effect at the end of the current billing period and you keep
          access until then.</p>
        <p><span className="text-white">Refunds:</span> fees already paid are non-refundable except as required by law. Promotional
          discounts (for example a one-time welcome-back offer) apply only to the billing period stated in the offer.</p>
        <p><span className="text-white">Failed payments:</span> if a renewal payment fails we will retry and notify you; continued failure
          may result in the account moving to a past-due state and paid features being paused until payment succeeds. Your data is
          retained per Section 6.</p>
        <p><span className="text-white">Price changes:</span> we may change pricing with at least 30 days notice; changes apply from your
          next billing period.</p>
      </Section>

      <Section title="6. Your data">
        <p>You retain ownership of all data you enter into FreightPilot AI. We store and process it solely to provide the service.
          On termination, you may export your data within 30 days after which it is permanently deleted.</p>
      </Section>

      <Section title="7. Service availability">
        <p>We aim for high uptime but do not guarantee uninterrupted service. Scheduled maintenance will be announced in advance
          when possible.</p>
      </Section>

      <Section title="8. SMS text messaging terms">
        <p>FreightPilot AI offers optional SMS text notifications — load alerts, weekly performance recaps, dispatch messages,
          and account notifications. You opt in through your in-app notification settings. Consent to receive texts is not a
          condition of purchasing any service.</p>
        <p>Message frequency varies based on your account activity. Message and data rates may apply — check with your mobile
          carrier. Mobile carriers are not liable for delayed or undelivered messages.</p>
        <p>Text <span className="text-white">STOP</span> to cancel at any time — you will receive a final confirmation message.
          Text <span className="text-white">HELP</span> for help, or contact us at contact@freightpilotai.ai or (605) 679-7303.</p>
        <p>Your mobile number and opt-in data are handled per our <Link to="/privacy" className="text-[#FF5A1F] hover:text-[#FF7038]">Privacy
          Policy</Link> and are never shared with third parties for marketing purposes.</p>
      </Section>

      <Section title="9. Disclaimer of warranties">
        <p>FreightPilot AI is provided "as is." We disclaim all warranties, express or implied, including merchantability, fitness for a
          particular purpose, and non-infringement.</p>
      </Section>

      <Section title="10. Limitation of liability">
        <p>To the maximum extent permitted by law, FreightPilot AI LLC and its founders are not liable for indirect, incidental, or
          consequential damages, including lost profits, arising out of your use of the service. Our total liability is limited to the
          fees you paid in the prior 12 months.</p>
      </Section>

      <Section title="11. Termination">
        <p>Either party may terminate at any time. We may suspend accounts that violate these Terms.</p>
      </Section>

      <Section title="12. Governing law">
        <p>These Terms are governed by the laws of the State of South Dakota, without regard to conflict-of-law principles.
          Any dispute will be resolved in the courts of Minnehaha County, South Dakota.</p>
      </Section>

      <Section title="13. Contact">
        <p>contact@freightpilotai.ai · (605) 679-7303 · Freight Pilot AI LLC, Sioux Falls, South Dakota.</p>
      </Section>
    </>
  );
}
